import type { Market } from './markets.js';

/**
 * Domain names to offer for a new site (ADR-0059): from its name, for its
 * market — the country's own domain first, then .com — plainly, run
 * together, and with a few endings. Only suggestions: 2d2s says which are
 * free, and the owner may edit the list before it is checked.
 */

const CYRILLIC: Readonly<Record<string, string>> = {
  а: 'a', б: 'b', в: 'v', г: 'g', ґ: 'g', д: 'd', е: 'e', ё: 'e', є: 'ye', ж: 'zh', з: 'z', и: 'i', і: 'i', ї: 'yi', й: 'y',
  к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh',
  щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};

/** Letters NFKD does not take apart into a Latin letter and a mark. */
const LATIN: Readonly<Record<string, string>> = { ß: 'ss', æ: 'ae', ø: 'o', ł: 'l', đ: 'd', þ: 'th', œ: 'oe', ı: 'i' };

/** Endings tried after the name itself, the way news and shop sites are named. */
export const DOMAIN_NAME_ENDINGS = ['news', '24', 'online', 'daily'] as const;

/** How many names are offered at most. */
export const DOMAIN_CANDIDATES_MAX = 12;

/** Where the country's own domain is not its ISO code. */
const COUNTRY_TLD: Readonly<Record<string, string>> = { GB: 'uk' };

/** «Кофе & Чай» → «kofe-and-chay»: the part of a domain name a site's name gives, or '' when it gives none. */
export function domainLabel(name: string): string {
  const latin = Array.from(name.toLowerCase())
    .map((char) => CYRILLIC[char] ?? LATIN[char] ?? char)
    .join('')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ');
  return latin
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50)
    .replace(/-+$/g, '');
}

/** The country's own top-level domain: `de` for Germany, `uk` for Great Britain. */
export function countryTld(country: string): string {
  return COUNTRY_TLD[country] ?? country.toLowerCase();
}

/**
 * Names to check for a new site: the country's domain before .com, the name
 * as written before run together, before the endings. Empty when the name
 * gives no Latin letters (Japanese, Korean) — then the owner types one.
 */
export function domainCandidates(name: string, market: Pick<Market, 'country'> | null): string[] {
  const label = domainLabel(name);
  if (!label) return [];
  const compact = label.replace(/-/g, '');
  const tlds = [...new Set([...(market ? [countryTld(market.country)] : []), 'com'])];
  // An ending the name already has is not added again: «Berlin News» is not «berlinnewsnews».
  const endings = DOMAIN_NAME_ENDINGS.filter((ending) => !compact.endsWith(ending));
  const labels = [...new Set([label, compact, ...endings.map((ending) => `${compact.slice(0, 50 - ending.length)}${ending}`)])];
  const names: string[] = [];
  for (const each of labels) {
    for (const tld of tlds) {
      if (names.length < DOMAIN_CANDIDATES_MAX) names.push(`${each}.${tld}`);
    }
  }
  return names;
}
